import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import { useHelp } from '../../../context/HelpContext';
import { useModules } from '../../../context/LevelsContext';

const LevelSelectorModal = ({ prefs, onSave, onClose }) => {
    const { user } = useAuth();
    const { language } = useLanguage();
    const { modules } = useModules();
    const nonPackModules = modules.filter(m => !m.isPack);
    const { startTour, hasSeenPage, helpPrefs, isPrefsSynced, registerOverlay, unregisterOverlay } = useHelp();
    const listRef = useRef(null);

    // Register as help overlay + auto-trigger tour
    useEffect(() => {
        registerOverlay('level-selector');
        return () => unregisterOverlay('level-selector');
    }, [registerOverlay, unregisterOverlay]);

    useEffect(() => {
        if (!isPrefsSynced) return;
        if (helpPrefs.globalEnabled && helpPrefs.autoTrigger && !hasSeenPage('level-selector')) {
            const timer = setTimeout(() => startTour('level-selector'), 500);
            return () => clearTimeout(timer);
        }
    }, [isPrefsSynced, helpPrefs.globalEnabled, helpPrefs.autoTrigger, hasSeenPage, startTour]);

    // --- Active level detection ---
    const activeLevelsArray = Array.isArray(user?.activeLevels)
        ? user.activeLevels
        : Object.values(user?.activeLevels || {});

    const activeIds = new Set(
        activeLevelsArray.map(l => {
            const id = String(l.stageId || l.var || '').toLowerCase();
            if (l.nroNiveau === '1' || l.stageId === 'pstar') return 'pstar';
            if (l.nroNiveau === '2' || l.stageId === 'ppl') return 'ppl';
            return id;
        })
    );

    const everPurchased = new Set(user?.everPurchasedLevels || []);

    // --- Order state (non-pack IDs) ---
    const [order, setOrder] = useState(() => {
        const saved = prefs.sidebarOrder || [];
        const allIds = nonPackModules.map(m => m.id);
        const merged = saved.filter(id => allIds.includes(id));
        allIds.forEach(id => { if (!merged.includes(id)) merged.push(id); });
        return merged;
    });

    // --- Added levels (manually checked by user) ---
    const [added, setAdded] = useState(() => new Set(prefs.addedLevels || []));

    // --- Hidden levels (explicitly hidden by user, even if active) ---
    const [hidden, setHidden] = useState(() => new Set(prefs.hiddenLevels || []));

    // --- Two-step flow ---
    const [step, setStep] = useState(1);

    // --- Type filter (step 1 only) ---
    const typeConfig = {
        aeroplane: { label: language === 'fr' ? 'Avion' : 'Airplane', icon: 'subject-aeroplane' },
        helicopter: { label: language === 'fr' ? 'Hélicoptère' : 'Helicopter', icon: 'subject-helicopter' },
        glider: { label: language === 'fr' ? 'Planeur' : 'Glider', icon: 'subject-glider' },
        dispatch: { label: 'Dispatch', icon: 'subject-dispatch' }
    };

    const uniqueTypes = Array.from(
        new Set(nonPackModules.flatMap(m => m.types && Array.isArray(m.types) ? m.types : []))
    ).sort((a, b) => {
        const order = ['aeroplane', 'helicopter', 'glider', 'dispatch'];
        return order.indexOf(a) - order.indexOf(b);
    });

    const [selectedType, setSelectedType] = useState(uniqueTypes[0] || 'aeroplane');

    // Is the level visible by default (PSTAR or active subscription)?
    const isDefaultVisible = (moduleId) => ['pstar', 'ppl', 'croa'].includes(moduleId) || activeIds.has(moduleId);

    // Is the level shown in sidebar?
    const isInSidebar = (moduleId) => (isDefaultVisible(moduleId) || added.has(moduleId)) && !hidden.has(moduleId);

    const getStatus = (moduleId, isFree) => {
        if (isFree) return { label: language === 'fr' ? 'Gratuit' : 'Free', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
        if (activeIds.has(moduleId)) return { label: language === 'fr' ? 'Actif' : 'Active', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
        if (everPurchased.has(moduleId)) return { label: language === 'fr' ? 'Expiré' : 'Expired', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' };
        return { label: language === 'fr' ? 'Disponible' : 'Available', color: 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400' };
    };

    const toggleAdded = (moduleId) => {
        const currentlyVisible = isInSidebar(moduleId);
        if (currentlyVisible) {
            // Hide it
            setHidden(prev => { const next = new Set(prev); next.add(moduleId); return next; });
            setAdded(prev => { const next = new Set(prev); next.delete(moduleId); return next; });
        } else {
            // Show it
            setHidden(prev => { const next = new Set(prev); next.delete(moduleId); return next; });
            if (!isDefaultVisible(moduleId)) {
                setAdded(prev => { const next = new Set(prev); next.add(moduleId); return next; });
            }
        }
    };

    // --- HTML5 Drag & Drop (desktop) — uses fullIdx for correct index mapping ---
    const dragRef = useRef(null);
    const [dragOverIdx, setDragOverIdx] = useState(null);
    const [dragIdx, setDragIdx] = useState(null);

    const handleDragStart = (e, fullIdx) => {
        dragRef.current = fullIdx;
        setDragIdx(fullIdx);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(fullIdx));
    };

    const handleDragOver = useCallback((e, fullIdx) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIdx(fullIdx);
    }, []);

    const handleDrop = useCallback((e, fullIdx) => {
        e.preventDefault();
        const fromFullIdx = dragRef.current;
        if (fromFullIdx === null || fromFullIdx === fullIdx) return;
        setOrder(prev => {
            const next = [...prev];
            const [moved] = next.splice(fromFullIdx, 1);
            const adjusted = fullIdx > fromFullIdx ? fullIdx - 1 : fullIdx;
            next.splice(adjusted, 0, moved);
            return next;
        });
    }, []);

    const handleDragEnd = useCallback(() => {
        dragRef.current = null;
        setDragIdx(null);
        setDragOverIdx(null);
    }, []);

    // --- Touch Drag (mobile) — uses fullIdx, computes positions inside setOrder updater ---
    const touchDrag = useRef(null);
    const [touchDragIdx, setTouchDragIdx] = useState(null);

    const handleTouchStart = useCallback((fullIdx, e) => {
        const touch = e.touches[0];
        const rowEl = e.currentTarget.closest('[data-drag-idx]');
        touchDrag.current = {
            fullIdx,
            startY: touch.clientY,
            rowHeight: rowEl?.offsetHeight || 64,
        };
        setTouchDragIdx(fullIdx);
    }, []);

    const handleTouchMove = useCallback((e) => {
        if (!touchDrag.current) return;
        e.preventDefault();

        const touch = e.touches[0];
        const delta = touch.clientY - touchDrag.current.startY;
        const rowHeight = touchDrag.current.rowHeight;
        const rowsToMove = Math.round(delta / rowHeight);

        if (rowsToMove !== 0) {
            setOrder(prev => {
                const fromFullIdx = touchDrag.current.fullIdx;

                // Build visible items from the latest order
                const visibleItems = prev
                    .map((id, fi) => ({ id, fullIdx: fi }))
                    .filter(item => {
                        const mod = nonPackModules.find(m => m.id === item.id);
                        return mod && isInSidebar(item.id);
                    });

                const currentVisibleIdx = visibleItems.findIndex(it => it.fullIdx === fromFullIdx);
                if (currentVisibleIdx === -1) return prev;

                const targetVisibleIdx = Math.max(0, Math.min(visibleItems.length - 1, currentVisibleIdx + rowsToMove));
                if (currentVisibleIdx === targetVisibleIdx) return prev;

                const targetFullIdx = visibleItems[targetVisibleIdx].fullIdx;
                const next = [...prev];
                const [moved] = next.splice(fromFullIdx, 1);
                const adjusted = targetFullIdx > fromFullIdx ? targetFullIdx - 1 : targetFullIdx;
                next.splice(adjusted, 0, moved);

                // Update ref for next move
                touchDrag.current.fullIdx = adjusted;
                touchDrag.current.startY = touch.clientY;
                setTouchDragIdx(adjusted);

                return next;
            });
        }
    }, [added, hidden]);

    const handleTouchEnd = useCallback(() => {
        touchDrag.current = null;
        setTouchDragIdx(null);
    }, []);

    // Attach touch listeners to scrollable list for passive: false (needed for preventDefault)
    useEffect(() => {
        const listEl = listRef.current;
        if (!listEl) return;
        const onMove = (e) => { if (touchDrag.current) handleTouchMove(e); };
        const onEnd = () => handleTouchEnd();
        listEl.addEventListener('touchmove', onMove, { passive: false });
        listEl.addEventListener('touchend', onEnd);
        return () => {
            listEl.removeEventListener('touchmove', onMove);
            listEl.removeEventListener('touchend', onEnd);
        };
    }, [handleTouchMove, handleTouchEnd]);

    // --- Save ---
    const handleSave = () => {
        onSave(order, [...added], [...hidden]);
        onClose();
    };

    // --- Computed lists ---

    // Step 1: filtered by type, for checkboxes (selection)
    const step1Modules = order
        .map(id => nonPackModules.find(m => m.id === id))
        .filter(Boolean)
        .filter(m => m.types && m.types.includes(selectedType));

    // Step 2: all visible items with fullIdx for correct drag mapping (ordering)
    const step2Items = order
        .map((id, fullIdx) => ({ id, fullIdx, module: nonPackModules.find(m => m.id === id) }))
        .filter(item => item.module && isInSidebar(item.id));

    const visibleCount = order.filter(id => isInSidebar(id)).length;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white dark:bg-[#121212] rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col animate-in zoom-in-95 duration-200 border border-gray-200 dark:border-white/10">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-white/10">
                    <div className="flex items-center gap-3">
                        {step === 2 && (
                            <button
                                onClick={() => setStep(1)}
                                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        )}
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                {step === 1
                                    ? (language === 'fr' ? 'Choisir mes niveaux' : 'Choose my levels')
                                    : (language === 'fr' ? 'Organiser' : 'Organize')
                                }
                            </h2>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                {step === 1
                                    ? (language === 'fr' ? 'Cochez les niveaux à afficher dans le menu' : 'Check levels to display in menu')
                                    : (language === 'fr' ? 'Glissez pour réorganiser l\'ordre d\'affichage' : 'Drag to reorder display')
                                }
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Step indicator */}
                <div data-help="level-step-indicator" className="flex justify-center gap-2 py-2">
                    {[1, 2].map(s => (
                        <div
                            key={s}
                            className={`h-1.5 rounded-full transition-all duration-300 ${
                                s === step ? 'w-6 bg-blue-500' : 'w-1.5 bg-gray-300 dark:bg-gray-600'
                            }`}
                        />
                    ))}
                </div>

                {/* Content area — animated on step change */}
                <div key={step} className="flex-1 min-h-0 flex flex-col animate-in fade-in duration-200">

                    {/* Type Selector — step 1 only */}
                    {step === 1 && uniqueTypes.length > 1 && (
                        <div data-help="level-type-filter" className="px-4 pt-1 pb-1">
                            <div className="flex bg-gray-100 dark:bg-white/5 rounded-2xl p-1">
                                {uniqueTypes.map((typeId) => {
                                    const config = typeConfig[typeId];
                                    const isActive = selectedType === typeId;
                                    return (
                                        <button
                                            key={typeId}
                                            onClick={() => setSelectedType(typeId)}
                                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                                                isActive
                                                    ? 'bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
                                                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                                            }`}
                                        >
                                            <img
                                                src={`/img/subjects/${config.icon}.png`}
                                                alt={config.label}
                                                className={`w-5 h-5 dark:invert brightness-0 dark:brightness-100 ${isActive ? 'opacity-70 dark:opacity-90' : 'opacity-30'}`}
                                            />
                                            <span className="hidden sm:inline">{config.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Body — scrollable list */}
                    <div ref={listRef} data-help="level-list" className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5 custom-scrollbar">

                        {/* ========== STEP 1: Selection (no drag) ========== */}
                        {step === 1 && step1Modules.map((module) => {
                            const status = getStatus(module.id, module.isFree);
                            const checked = isInSidebar(module.id);

                            return (
                                <div
                                    key={module.id}
                                    onClick={() => toggleAdded(module.id)}
                                    className={`flex items-center gap-3 px-3 py-3 rounded-2xl transition-all select-none cursor-pointer
                                        ${!checked ? 'opacity-40' : ''}
                                        hover:bg-gray-50 dark:hover:bg-white/5
                                    `}
                                >
                                    {/* Checkbox */}
                                    <div
                                        className={`w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center transition-all
                                            ${checked
                                                ? 'bg-blue-500 border-blue-500'
                                                : 'border-gray-300 dark:border-gray-600'
                                            }
                                        `}
                                    >
                                        {checked && (
                                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>

                                    {/* Color pill */}
                                    <div
                                        className="min-w-10 min-h-10 px-2 py-1.5 rounded-xl flex items-center justify-center text-[10px] font-black shrink-0 shadow text-center leading-tight"
                                        style={{ backgroundColor: module.newBackgroundColor, color: '#000' }}
                                    >
                                        {module.title}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                {module.titleObj?.[language] || module.title}
                                            </span>
                                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${status.color}`}>
                                                {status.label}
                                            </span>
                                        </div>
                                        <span className="text-xs text-gray-500 dark:text-gray-500">
                                            {module.subtitleObj?.[language] || module.subtitle}
                                        </span>
                                    </div>

                                    {/* Lock icon for non-purchased levels */}
                                    {!module.isFree && !activeIds.has(module.id) && (
                                        <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    )}
                                </div>
                            );
                        })}

                        {/* ========== STEP 2: Ordering (drag, no checkboxes) ========== */}
                        {step === 2 && step2Items.map((item, renderIdx) => {
                            const { module, fullIdx } = item;
                            const isDragging = dragIdx === fullIdx || touchDragIdx === fullIdx;
                            const isDragOver = dragOverIdx === fullIdx && dragIdx !== null && dragIdx !== fullIdx;

                            return (
                                <div
                                    key={item.id}
                                    data-drag-idx={fullIdx}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, fullIdx)}
                                    onDragOver={(e) => handleDragOver(e, fullIdx)}
                                    onDrop={(e) => handleDrop(e, fullIdx)}
                                    onDragEnd={handleDragEnd}
                                    className={`flex items-center gap-3 px-3 py-3 rounded-2xl transition-all select-none
                                        ${isDragging ? 'opacity-50 scale-[0.98] bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-300 dark:ring-blue-500/30' : 'hover:bg-gray-50 dark:hover:bg-white/5'}
                                        ${isDragOver ? 'border-t-2 border-blue-400 dark:border-blue-500' : 'border-t-2 border-transparent'}
                                    `}
                                >
                                    {/* Drag handle */}
                                    <div
                                        className="cursor-grab active:cursor-grabbing touch-none text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 transition-colors shrink-0"
                                        onTouchStart={(e) => handleTouchStart(fullIdx, e)}
                                    >
                                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                            <circle cx="9" cy="6" r="1.5" />
                                            <circle cx="15" cy="6" r="1.5" />
                                            <circle cx="9" cy="12" r="1.5" />
                                            <circle cx="15" cy="12" r="1.5" />
                                            <circle cx="9" cy="18" r="1.5" />
                                            <circle cx="15" cy="18" r="1.5" />
                                        </svg>
                                    </div>

                                    {/* Color pill */}
                                    <div
                                        className="min-w-10 min-h-10 px-2 py-1.5 rounded-xl flex items-center justify-center text-[10px] font-black shrink-0 shadow text-center leading-tight"
                                        style={{ backgroundColor: module.newBackgroundColor, color: '#000' }}
                                    >
                                        {module.title}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate block">
                                            {module.titleObj?.[language] || module.title}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-500">
                                            {module.subtitleObj?.[language] || module.subtitle}
                                        </span>
                                    </div>

                                    {/* Position number */}
                                    <span className="text-xs font-bold text-gray-300 dark:text-gray-600 tabular-nums shrink-0 w-5 text-center">
                                        {renderIdx + 1}
                                    </span>
                                </div>
                            );
                        })}

                        {/* Empty state — step 2 with nothing selected */}
                        {step === 2 && step2Items.length === 0 && (
                            <div className="text-center py-12">
                                <p className="text-sm text-gray-400 dark:text-gray-600">
                                    {language === 'fr' ? 'Aucun niveau sélectionné' : 'No levels selected'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div data-help="level-footer" className="px-6 py-4 border-t border-gray-200 dark:border-white/10 flex items-center justify-between">
                    {step === 1 ? (
                        <>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            >
                                {language === 'fr' ? 'Annuler' : 'Cancel'}
                            </button>
                            <button
                                onClick={() => setStep(2)}
                                disabled={visibleCount === 0}
                                className={`px-6 py-2.5 text-sm font-semibold rounded-xl shadow-lg transition-all active:scale-[0.97]
                                    ${visibleCount > 0
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/25 hover:shadow-blue-500/40'
                                        : 'bg-gray-200 dark:bg-white/10 text-gray-400 dark:text-gray-600 cursor-not-allowed shadow-none'
                                    }
                                `}
                            >
                                {language === 'fr' ? `Suivant (${visibleCount})` : `Next (${visibleCount})`}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={() => setStep(1)}
                                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            >
                                {language === 'fr' ? 'Retour' : 'Back'}
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-lg shadow-blue-500/25 transition-all hover:shadow-blue-500/40 active:scale-[0.97]"
                            >
                                {language === 'fr' ? 'Enregistrer' : 'Save'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LevelSelectorModal;
